import {
  TextField,
  Button,
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  Divider,
  Grid,
} from "@mui/material";
import {
  ChangeEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Controller, useForm } from "react-hook-form";
import { Post } from "../../../__generated__/graphql";
import { DELETE_POST, EXPORT_POST, UPDATE_POST } from "../graphql";
import { useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { confirmAlert } from "react-confirm-alert";
import { TabbedPage } from "../../../components/TabbedPage";
import { Note } from "../../../components/Note";
import dayjs from "dayjs";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { SocialLinks } from "../SocialLinks";
import { ZonedDateTimePicker } from "../../../components/ZonedDateTimePicker";
import MarkdownEditor from "../../../components/MarkdownEditor";
import { ErrorRenderer } from "../../../components/ErrorRenderer";
import { PollPicker } from "../../../components/PollPicker";
import { NavigationBlocker } from "../../../components/NavigationBlocker";
import { getUpdatedImageUrl } from "./helpers";
import PostLinksList from "../../band/links/PostLinksList";
import { ImageSize } from "../../bands";
import { ImagePreview } from "../../../components/ImagePreview";
import { getImageAspectRatio } from "../../../utils/getImageAspectRatio";
import { callCompressor } from "../../band/imageEdit/Upload";

const schema = yup
  .object({
    title: yup.string().required(),
    description: yup.string().required(),
    date: yup.string().required(),
    image: yup.string(),
    socials: yup.object({
      facebook: yup.string().url().optional(),
      tiktok: yup.string().url().optional(),
      twitter: yup.string().url().optional(),
      instagram: yup.string().url().optional(),
    }),
    pollId: yup.string().nullable(),
    aspect: yup.number().required(),
  })
  .required();
interface IPostFormProps {
  post: Post;
}

export const PostForm = ({ post }: IPostFormProps) => {
  const navigate = useNavigate();
  const [imageUrl, setImageUrl] = useState(post.image ?? "");
  const [aspect, setAspect] = useState(post.aspect ?? 16 / 9);
  const [tabIndex, setTabIndex] = useState(0);
  const {
    register,
    handleSubmit,
    watch,
    formState: { isDirty },
    formState: { errors },
    setValue,
    resetField,
    control,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      date: post.date,
      socials: post.socials,
      description: post.description,
      title: post.title,
      pollId: post.pollId ?? undefined,
      aspect: post.aspect ?? 1,
    },
  });
  const imageFieldRegistration = register("image");
  const aspectRequestId = useRef(0);
  const updateAspectFromUrl = useCallback(
    async (url?: string | null) => {
      const trimmed = url?.trim();
      if (!trimmed) {
        return;
      }

      const requestId = ++aspectRequestId.current;
      const resolvedUrl = getUpdatedImageUrl(trimmed) ?? trimmed;
      const ratio = await getImageAspectRatio(resolvedUrl);

      if (!ratio || requestId !== aspectRequestId.current) {
        return;
      }

      setAspect(ratio);
      setValue("aspect", ratio, {
        shouldValidate: false,
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [setValue]
  );
  const handleImageUrlChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const nextUrl = event.target.value;
      setImageUrl(nextUrl);
      updateAspectFromUrl(nextUrl);
    },
    [updateAspectFromUrl]
  );
  const location = useLocation();
  const updatedImageUrl = location?.state?.updatedImageUrl;
  const updatedAspectRatio = location?.state?.aspectRatio;

  const [deletePost] = useMutation(DELETE_POST, {
    onCompleted: () => {
      toast("post deleted");
      navigate("/news");
    },
  });

  const [updatePost, { loading: updateLoading, error }] = useMutation(
    UPDATE_POST,
    {
      onCompleted: () => {
        toast("Update complete");
        navigate(location.pathname, { replace: true });
      },
      refetchQueries: ["post"],
    }
  );

  console.log("update post error", error);

  const [exportPost, { loading: exportLoading, error: exportError }] =
    useMutation(EXPORT_POST, {
      onCompleted: () => {
        toast("publish complete");
      },
    });

  useEffect(() => {
    if (!updatedImageUrl) {
      return;
    }

    const nextUrl = updatedImageUrl;
    setImageUrl(nextUrl);
    setValue("image", nextUrl, {
      shouldValidate: false,
      shouldDirty: true,
      shouldTouch: true,
    });

    if (updatedAspectRatio) {
      ++aspectRequestId.current;
      const ratio = updatedAspectRatio;
      setAspect(ratio);
      setValue("aspect", ratio, {
        shouldValidate: false,
        shouldDirty: true,
        shouldTouch: true,
      });
    } else {
      updateAspectFromUrl(nextUrl);
    }
  }, [updatedImageUrl, updatedAspectRatio, setValue, updateAspectFromUrl]);

  // const imageUrl =  ?? ;
  const onSubmit = (data: Partial<Post>) => {
    const { title, description, date, image, pollId } = data;

    updatePost({
      variables: {
        id: post.id,
        title: title!,
        description: description!,
        date,
        image: imageUrl,
        socials: data.socials,
        pollId: pollId ? pollId : null,
        aspect,
      },
    });
  };

  const handleExport = async () => {
    await exportPost({
      variables: {
        postId: post.id,
      },
    });
  };

  const handleDelete = () => {
    confirmAlert({
      title: "Delete this Post?",
      message: "Are you sure? this can't be undone",
      buttons: [
        {
          label: "Yes",
          onClick: () => deletePost({ variables: { postId: post.id } }),
        },
        {
          label: "No",
          onClick: () => {},
        },
      ],
    });
  };

  const handleNotification = async () => {
    await handleSubmit(onSubmit);

    await handleExport();

    setTimeout(() => navigate(`/notifications?postId=${post.id}`), 2000);
  };

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const imageKey = location?.state?.updated ?? "imageUrl";
  const watchedPollId = watch("pollId");

  const headerActions = (
    <Stack
      direction="row"
      spacing={1.5}
      useFlexGap
      flexWrap="wrap"
      alignItems="center"
    >
      <Button type="submit" variant="contained" form="post-details-form">
        Save
      </Button>
      <Button variant="contained" color="secondary" onClick={handleExport}>
        Publish post
      </Button>
      <Button variant="contained" color="error" onClick={handleDelete}>
        Delete post
      </Button>
      <Button variant="contained" color="success" onClick={handleNotification}>
        Send notification
      </Button>
    </Stack>
  );

  const postDetailsContent = (
    <>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        id="post-details-form"
        sx={{ display: "contents" }}
      >
        <Stack spacing={3}>
          <Card elevation={1}>
            <CardContent>
              <Grid container spacing={4} alignItems="flex-start">
                <Grid item xs={12} md={4}>
                  <Stack spacing={2}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                    >
                      Featured Image
                    </Typography>
                    <ImagePreview
                      key={imageKey}
                      src={getUpdatedImageUrl(imageUrl)}
                      alt="Post hero"
                      onClick={() => {
                        navigate("/image", {
                          state: {
                            postId: post.id,
                          },
                        });
                      }}
                    />
                    <ImageSize imageUrl={imageUrl} />
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          navigate("/image", {
                            state: {
                              postId: post.id,
                            },
                          });
                        }}
                      >
                        Update Image
                      </Button>
                      <Button
                        variant="outlined"
                        color="secondary"
                        disabled={!imageUrl}
                        onClick={async () => {
                          if (!imageUrl) return;
                          const url = await callCompressor(imageUrl, true);
                          setImageUrl(url);
                          setValue("image", url, {
                            shouldValidate: false,
                            shouldDirty: true,
                            shouldTouch: true,
                          });
                        }}
                      >
                        Compress
                      </Button>
                    </Stack>
                    <TextField
                      key={"aspect"}
                      name="aspect"
                      disabled
                      value={aspect}
                      label="Aspect Ratio"
                      fullWidth
                      margin="dense"
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      id="post-image"
                      label="Image URL"
                      variant="outlined"
                      defaultValue={post?.image}
                      {...imageFieldRegistration}
                      onChange={(event) => {
                        imageFieldRegistration.onChange(event);
                        handleImageUrlChange(event);
                      }}
                      fullWidth
                      margin="dense"
                      error={Boolean(errors.image)}
                      helperText={errors.image?.message}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Stack spacing={2}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                    >
                      Post Details
                    </Typography>
                    <TextField
                      id="post-title"
                      label="Title"
                      variant="outlined"
                      defaultValue={post?.title}
                      {...register("title")}
                      fullWidth
                      margin="dense"
                      error={Boolean(errors.title)}
                      helperText={errors.title?.message}
                    />
                    <Controller
                      control={control}
                      name="date"
                      render={({ field: { onChange, onBlur, value, ref } }) => (
                        <ZonedDateTimePicker
                          format="YYYY/MM/DD HH:mm"
                          ampm={false}
                          label="Publish Date"
                          // @ts-ignore
                          defaultValue={dayjs(post.date)}
                          // @ts-ignore
                          onChange={onChange} // send value to hook form
                        />
                      )}
                    />
                    {errors.date?.message && (
                      <Typography variant="caption" color="error">
                        {errors.date?.message}
                      </Typography>
                    )}
                    <Note
                      message={`Note: you can schedule news posts by picking a date in the future - just publish the item as normal - the app won't load the news until the scheduled date and TIME`}
                      style={{ marginTop: -16 }}
                    />
                  </Stack>
                </Grid>
              </Grid>
              <Divider sx={{ my: 4 }} />
              <Stack spacing={3}>
                <div>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                  >
                    Content
                  </Typography>
                  <Controller
                    control={control}
                    {...register("description")}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <MarkdownEditor text={value ?? ""} onChange={onChange} />
                    )}
                  />
                  {errors.description?.message && (
                    <Typography variant="caption" color="error">
                      {errors.description?.message}
                    </Typography>
                  )}
                </div>
                <Controller
                  control={control}
                  name="socials"
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <SocialLinks
                      data={value}
                      // @ts-ignore
                      onChange={onChange}
                      errors={errors.socials}
                    />
                  )}
                />
              </Stack>
            </CardContent>
          </Card>

          <Card elevation={1} sx={{ overflow: "visible" }}>
            <CardContent>
              <Stack spacing={2}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                >
                  Attached Poll
                </Typography>
                <Controller
                  control={control}
                  {...register("pollId")}
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <PollPicker
                      value={value}
                      handleChange={(pollId: string, option: string) =>
                        onChange(pollId)
                      }
                      // errors={errors.pollId}
                    />
                  )}
                />
                {watchedPollId && (
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    alignItems="flex-start"
                  >
                    <Button
                      variant="outlined"
                      onClick={() => {
                        navigate(`/poll/edit/${watchedPollId}`);
                      }}
                    >
                      View Poll
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setValue("pollId", null);
                      }}
                    >
                      Remove
                    </Button>
                  </Stack>
                )}
              </Stack>
            </CardContent>
          </Card>

          <ErrorRenderer errors={errors} />
        </Stack>
      </Box>
      <Note message={"Changes won't be seen until you publish"} />
    </>
  );

  const linkedItemsContent = (
    <Stack spacing={3}>
      <Card elevation={1}>
        <CardContent>
          <Stack spacing={2}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
            >
              Linked Items
            </Typography>
            <PostLinksList post={post} disableContainer />
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );

  return (
    <>
      <NavigationBlocker isDirty={isDirty} />
      <TabbedPage
        title={"Edit Post"}
        actions={headerActions}
        value={tabIndex}
        onChange={handleTabChange}
        tabs={[
          {
            label: "Post Details",
            id: "post-tab-0",
            ariaControlsId: "post-tabpanel-0",
            content: postDetailsContent,
          },
          {
            label: "Linked Items",
            id: "post-tab-1",
            ariaControlsId: "post-tabpanel-1",
            content: linkedItemsContent,
          },
        ]}
      />
    </>
  );
};
